
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useNotify } from "@/hooks/useNotify";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Eye, EyeOff, AlertCircle } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const LoginForm = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loginError, setLoginError] = useState<string | null>(null);
  const { signInWithEmailAndPassword, isLoading } = useAuth();
  const notify = useNotify();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoginError(null); // Clear any previous errors
    
    try {
      await signInWithEmailAndPassword(email, password);
      // Navigation is handled entirely by AuthContext
    } catch (error: any) {
      console.log("Error during login process:", error);
      
      // Check if it's an email not confirmed error
      if (error.message?.includes('Email not confirmed') || 
          error.message?.includes('email_not_confirmed') ||
          error.name === 'AuthApiError' && error.message?.includes('Email not confirmed')) {
        console.log("Email not confirmed - redirecting to verification");
        
        setLoginError("Please verify your email address to continue.");
        notify.info("Please verify your email address to continue. You can request a new verification code on the next screen.");
        
        // Navigate to verification screen with email (but don't auto-send)
        navigate('/verify', { state: { email } });
      } else {
        // Handle other login errors with user-friendly message
        const errorMessage = "The email or password you entered is incorrect. Please check your credentials and try again.";
        setLoginError(errorMessage);
        notify.error(errorMessage);
      }
    }
  };

  return (
    <Card className="w-full max-w-md">
      <CardHeader>
        <CardTitle className="text-2xl">Login</CardTitle>
        <CardDescription>
          Enter your credentials to access your account
        </CardDescription>
      </CardHeader>
      <form onSubmit={handleSubmit}>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              placeholder="name@example.com"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                setLoginError(null); // Clear error when user types
              }}
              required
              className={loginError ? "border-destructive" : ""}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="password">Password</Label>
            <div className="relative">
              <Input
                id="password"
                type={showPassword ? "text" : "password"}
                placeholder="••••••••"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  setLoginError(null); // Clear error when user types
                }}
                required
                className={loginError ? "pr-10 border-destructive" : "pr-10"}
              />
              <button 
                type="button"
                onClick={() => setShowPassword(!showPassword)} 
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                tabIndex={-1}
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>
          
          {loginError && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{loginError}</AlertDescription>
            </Alert>
          )}
        </CardContent>
        <CardFooter className="flex flex-col space-y-4">
          <Button type="submit" className="w-full" disabled={isLoading}>
            {isLoading ? "Logging in..." : "Log in"}
          </Button>
          
          <div className="flex justify-between w-full text-sm">
            <Link to="/forgot-password" className="text-blue-400 hover:underline">
              Forgot password?
            </Link>
            <Link to="/verify" className="text-blue-400 hover:underline">
              Verify account
            </Link>
          </div>
          
          <div className="text-sm text-center">
            Don't have an account?{" "}
            <Link to="/signup" className="text-blue-400 hover:underline font-medium">
              Create an account
            </Link>
          </div>
        </CardFooter>
      </form>
    </Card>
  );
};

export default LoginForm;
